import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';

export default function SOC2ChangeManagementEvidencePage() {
  const faqs = [
    {
      question: 'What counts as SOC 2 change management evidence?',
      answer:
        'SOC 2 CC8.1 requires proof that code changes were reviewed and approved before production deployment. Auditors look for pull request approval records with reviewer identities, approval timestamps, required CI check results that passed before merge, branch protection configuration snapshots, and exception records for any emergency changes that bypassed normal controls.',
    },
    {
      question: 'What is SOC 2 Type II and how does it differ from Type I?',
      answer:
        'SOC 2 Type I is a point-in-time assessment — it confirms that controls were designed and in place at a specific date. Type II covers a 6–12 month period and requires evidence that controls operated consistently throughout. For GitHub, this means pull request approvals, CI checks, and branch protection snapshots continuously captured across the entire audit period — not screenshots taken the week before the audit.',
    },
    {
      question: 'How many pull requests do SOC 2 auditors sample?',
      answer:
        'For a SOC 2 Type II audit, auditors typically sample 20–30 pull requests from across the audit period. They select a mix of routine changes, deployments, and any emergency hotfixes. For each sample they want to see the PR, reviewer identity, approval timestamp, CI check results, and evidence that branch protection was enforced at time of merge.',
    },
    {
      question: 'Does EvidentTrail certify SOC 2 compliance?',
      answer:
        'No. EvidentTrail generates evidence packs that support SOC 2 audit reviews. Certification requires a licensed CPA firm (a SOC 2 auditor) to assess the evidence and issue the report. EvidentTrail eliminates the manual evidence collection work — not the auditor.',
    },
    {
      question: 'What happens when we need an emergency hotfix?',
      answer:
        'Emergency hotfixes that bypass normal branch protection create a SOC 2 exception. The exception needs to be documented with: the business justification for bypassing controls, who authorized the bypass, what compensating controls were applied, and what remediation was done afterward. EvidentTrail exception tracking captures the emergency merge event and provides a structured record for the documentation requirement.',
    },
    {
      question: 'Can I export evidence for auditor review?',
      answer:
        'Yes. Evidence packs can be downloaded in PDF, CSV, JSON, or manifest format with SHA-256 checksums for tamper-evident delivery to auditors. Guest mode allows auditors to access evidence directly without a permanent EvidentTrail account.',
    },
    {
      question: 'Does EvidentTrail integrate with Vanta or Drata?',
      answer:
        'EvidentTrail exports evidence in JSON format compatible with GRC platform ingestion. Evidence packs can be imported into Vanta, Drata, Secureframe, and similar platforms. EvidentTrail is focused specifically on the GitHub change control evidence layer — it complements rather than replaces GRC platforms.',
    },
  ];

  const longformSections = [
    {
      title: 'What SOC 2 CC8.1 requires from your GitHub workflow',
      paragraphs: [
        'SOC 2 Common Criteria 8.1 (CC8.1) states that "the entity authorizes, designs, develops or acquires, configures, documents, tests, approves, and implements changes to infrastructure, data, software, and procedures to meet its objectives." For software engineering teams, this translates to a code review and deployment process with documented approvals at each stage.',
        'In practice, auditors look for evidence that every production change went through a pull request, was reviewed by someone other than the author, passed required automated checks, and was not deployed until all approvals were complete. They also check that the process operated consistently — not just in the week before the audit.',
        'The challenge is that GitHub stores this information across pull request events, review events, status check events, and merge events — none of which are structured for auditor consumption. Correlating this data for 20–30 sampled PRs across a 12-month period is where teams spend 20–40 hours before each audit.',
      ],
      bullets: [
        'Every production PR must have at least one approval from a reviewer who is not the author',
        'Required CI status checks must pass before merge is permitted',
        'Branch protection configuration must prevent force pushes and direct commits',
        'Exception records required for any merge that bypasses normal controls',
      ],
    },
    {
      title: 'SOC 2 CC6.1: Logical Access Controls in GitHub',
      paragraphs: [
        'CC6.1 covers logical access security — who can access systems and how that access is managed. In GitHub, this means controlling who has write access to production branches, enforcing two-factor authentication, and ensuring that access is reviewed regularly.',
        'Auditors checking CC6.1 look for branch protection rules that restrict who can push to protected branches, evidence that 2FA is enforced at the organization level, access control lists showing that only authorized individuals have repository access, and records of access reviews.',
        'Branch protection drift is a CC6.1 risk that is often missed. If a developer temporarily disables branch protection to fix a build issue and forgets to re-enable it, the gap creates both a control failure and an audit finding. EvidentTrail drift detection captures this event with before/after configuration snapshots and a severity classification.',
      ],
      bullets: [
        'Branch protection rules restrict push access to authorized individuals only',
        '2FA enforcement at the GitHub organization level',
        'Access control review records for the audit period',
        'Drift detection: unauthorized changes to branch protection captured automatically',
      ],
    },
    {
      title: 'SOC 2 Type II: why continuous evidence matters',
      paragraphs: [
        'SOC 2 Type II is fundamentally different from Type I in one critical way: auditors must verify that controls operated consistently over time, not just that they were configured correctly at a point in time. This means a branch protection snapshot taken the day before the audit is insufficient — the auditor needs evidence that branch protection was in place and functioning throughout the 6–12 month audit period.',
        'For GitHub compliance, this means capturing pull request approvals, CI check results, and branch protection snapshots continuously — every event, every day, throughout the audit window. Manual evidence collection approaches fail this requirement because they are inherently point-in-time: screenshots taken retrospectively cannot prove that controls were operating six months ago.',
        'EvidentTrail is designed specifically for Type II requirements. The GitHub App installs once and begins capturing every relevant event immediately. When the audit window closes, the evidence pack covers the entire period with no gaps — auditors can sample any subset of the captured events and find consistent, structured evidence.',
      ],
      bullets: [
        'Type II requires continuous evidence across 6–12 months — not a pre-audit snapshot',
        'Auditors sample changes from across the period — gaps in evidence create findings',
        'Branch protection history is not stored by GitHub — EvidentTrail captures it at event time',
        'Evidence packs cover the full audit window from day one of installation',
      ],
    },
    {
      title: 'CC7.1 and CC7.2: Monitoring and Threat Detection',
      paragraphs: [
        'Beyond change management and access control, SOC 2 Common Criteria 7.1 (System Operations) and CC7.2 (Monitoring) require that organizations monitor for and detect threats to their systems. In GitHub, this translates to monitoring for unauthorized configuration changes, detecting secret scanning alerts, and recording evidence of ongoing monitoring.',
        'Branch protection drift events satisfy CC7.1 monitoring requirements — they demonstrate that the organization detects and records unauthorized changes to security controls. Secret scanning alerts address the threat detection aspect of CC7.2. Workflow change events — modifications to GitHub Actions pipelines — are additional monitoring signals that auditors may review.',
        'The combination of change control evidence (CC8.1), access control snapshots (CC6.1), and monitoring records (CC7.1/CC7.2) from a single GitHub evidence pack covers the majority of what auditors ask about in the software development context.',
      ],
      bullets: [
        'CC7.1: Branch protection drift events demonstrate monitoring for configuration changes',
        'CC7.2: Continuous capture provides the monitoring record auditors require',
        'Workflow drift: GitHub Actions configuration changes captured as monitoring events',
        'Secret scanning alerts: additional threat detection evidence for CC7.2',
      ],
    },
    {
      title: 'Common SOC 2 GitHub audit failures',
      paragraphs: [
        'The most frequently cited GitHub-related SOC 2 findings are: self-merges (developer approved and merged their own PR), force pushes to protected branches that overwrote history, missing CI check evidence for sampled changes, and emergency hotfixes with no documented exception record.',
        'Self-merges are detected by EvidentTrail because the evidence record links the PR author identity to the approver identity — mismatches are flagged. Force push events are captured as drift events. Missing CI evidence is visible in the evidence pack because the CI check field will be empty or show a failure. Emergency hotfixes are captured as exception records, prompting the documentation workflow.',
        'A less obvious failure mode is evidence that was correct at audit time but cannot be verified to represent the full audit period. Auditors increasingly ask to see evidence from specific dates rather than accepting a summary export. EvidentTrail stores timestamped records for every event, so auditors can request evidence from any date in the period and receive a verifiable record.',
      ],
      bullets: [
        'Self-merge: author and approver identity are the same — EvidentTrail flags this',
        'Force push: captured as a drift event with severity classification',
        'Missing CI evidence: visible gap in evidence pack — prompts investigation',
        'Undocumented exceptions: exception records created for every bypass event',
        'Non-verifiable period coverage: timestamped records for every event throughout the period',
      ],
    },
  ];

  return (
    <MarketingLayout
      title="SOC 2 Change Management Evidence from GitHub | EvidentTrail"
      description="Automate SOC 2 CC8.1 change management evidence — PR approvals, CI check results, branch protection snapshots — captured continuously from GitHub and exported as audit-ready packs."
      h1="SOC 2 Change Management Evidence"
      heroText="Stop manually screenshotting pull requests. Map your GitHub workflow directly to SOC 2 CC8.1 and CC6.1 — and capture Type II continuous evidence from day one."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'SOC 2 Change Management Evidence', url: 'https://evidenttrail.com/soc2-change-management-evidence' },
        ]}
      />
      <FaqJsonLd items={faqs} />

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">

            {/* SOC 2 Controls Covered */}
            <h2 className={`${tokens.heading.h2} mb-6`}>SOC 2 Controls Covered by GitHub Evidence</h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-12">
              {[
                {
                  control: 'CC8.1',
                  name: 'Change Management',
                  evidence: 'PR approvals, reviewer identity, CI check results, merge records, branch protection snapshots at time of change',
                },
                {
                  control: 'CC6.1',
                  name: 'Logical Access',
                  evidence: 'Branch protection rules, push restrictions, force push controls, access control drift detection records',
                },
                {
                  control: 'CC7.1',
                  name: 'System Operations',
                  evidence: 'Branch protection drift alerts, workflow configuration changes, secret scanning events',
                },
                {
                  control: 'CC7.2',
                  name: 'Monitoring',
                  evidence: 'Continuous capture of all change events — no gaps in monitoring throughout the audit period',
                },
              ].map(({ control, name, evidence }) => (
                <div key={control} className={tokens.card}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded text-xs font-mono">{control}</span>
                    <h3 className="text-white font-semibold">{name}</h3>
                  </div>
                  <p className="text-sm text-slate-400">{evidence}</p>
                </div>
              ))}
            </div>

            {/* CC8.1 Detail */}
            <h2 className={`${tokens.heading.h2} mb-6`}>CC8.1 Evidence: What Auditors Examine</h2>
            <div className="space-y-4 mb-12">
              {[
                {
                  label: 'Authorization',
                  standard: 'Change was formally authorized before implementation.',
                  evidence: 'Pull request creation record. Branch protection rules enforce minimum reviewer counts before merge is permitted.',
                },
                {
                  label: 'Testing',
                  standard: 'Change was tested before deployment to production.',
                  evidence: 'Required CI status check results captured with pass/fail and timestamp — linked to the specific PR.',
                },
                {
                  label: 'Approval',
                  standard: 'Change was explicitly approved by an authorized person.',
                  evidence: 'Reviewer identity, approval decision, and timestamp captured per PR. Author cannot self-approve — segregation of duty enforced.',
                },
                {
                  label: 'Documentation',
                  standard: 'A record of the change and its authorization must be maintained.',
                  evidence: 'Immutable evidence pack with SHA-256 checksum. PDF, CSV, and JSON formats for auditor consumption.',
                },
                {
                  label: 'Exception Handling',
                  standard: 'Emergency changes outside the normal process must be documented.',
                  evidence: 'Exception records capture emergency merges with lifecycle tracking, linked approvals, and remediation documentation.',
                },
              ].map(({ label, standard, evidence }) => (
                <div key={label} className={tokens.card}>
                  <div className="flex items-center justify-between mb-3 border-b border-slate-700 pb-2">
                    <h3 className="text-white font-bold">{label}</h3>
                    <span className="bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded text-xs font-mono">CC8.1</span>
                  </div>
                  <p className="text-slate-500 text-xs mb-2 italic">{standard}</p>
                  <div className="bg-slate-900/50 p-3 rounded-lg">
                    <span className="text-primary-400 text-xs font-medium">EvidentTrail evidence: </span>
                    <span className="text-slate-300 text-sm">{evidence}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Type I vs Type II */}
            <h2 className={`${tokens.heading.h2} mb-6`}>SOC 2 Type I vs Type II: What Changes</h2>
            <div className="overflow-x-auto mb-12">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Aspect</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Type I</th>
                    <th className="text-left py-3 px-4 text-primary-400 font-medium">Type II</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {[
                    ['Time period', 'Point in time', '6–12 months continuous'],
                    ['Branch protection evidence', 'Current configuration screenshot', 'Historical snapshots at each change event'],
                    ['PR approval evidence', 'Recent PRs only', 'Full audit period — 20–30 samples from any date'],
                    ['CI check evidence', 'Current pipeline configuration', 'All check results throughout period'],
                    ['Exception records', 'Policy document', 'Actual exception events with lifecycle records'],
                    ['EvidentTrail value', 'Point-in-time export', 'Full-period continuous capture — designed for Type II'],
                  ].map(([aspect, t1, t2]) => (
                    <tr key={aspect} className="hover:bg-slate-800/30">
                      <td className="py-3 px-4 text-slate-300">{aspect}</td>
                      <td className="py-3 px-4 text-slate-500">{t1}</td>
                      <td className="py-3 px-4 text-slate-300">{t2}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-center">
              <a href={appLinks.register} className="btn btn-primary px-8 py-3">
                Start SOC 2 Evidence Collection
              </a>
            </div>
          </div>
        </div>
      </section>

      <SeoLongformSections sections={longformSections} faqs={faqs} />

      <section className={tokens.section}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related SOC 2 Resources</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <Link to="/soc2-github-compliance" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">SOC 2 GitHub Guide</h4>
              <p className="text-sm text-slate-400">Complete GitHub configuration checklist and Type II evidence guide.</p>
            </Link>
            <Link to="/iso-27001-vs-soc2" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">ISO 27001 vs SOC 2</h4>
              <p className="text-sm text-slate-400">Pursue both frameworks with shared GitHub evidence — 60–70% overlap.</p>
            </Link>
            <Link to="/branch-protection-drift-detection" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Branch Protection Drift</h4>
              <p className="text-sm text-slate-400">Detect and record unauthorized branch protection changes for CC6.1.</p>
            </Link>
            <Link to="/tools/pr-approvals-report" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">PR Approvals Report</h4>
              <p className="text-sm text-slate-400">Free tool — generate a PR approvals evidence pack now.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
